import { RequirePermission } from "@/components/RequirePermission";
import { AuditLogTable } from "@/components/controller/AuditLogTable";
import { useOpsAuditLog } from "@/hooks/useOpsAuditLog";
import { useLedgerAuditLog } from "@/hooks/useLedgerAuditLog";
import { useFinancialAuditLog } from "@/hooks/useFinancialAuditLog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Download } from "lucide-react";
import { format } from "date-fns";
import { useState, useCallback } from "react";
import { useIsMobile } from "@/hooks/use-mobile";

const FINANCIAL_TABLES = [
  { value: "all", label: "All Tables" },
  { value: "accounting_transactions", label: "Transactions" },
  { value: "accounting_lines", label: "Ledger Lines" },
  { value: "bills", label: "Bills" },
  { value: "invoices", label: "Invoices" },
  { value: "approval_requests", label: "Approvals" },
  { value: "lien_waivers", label: "Lien Waivers" },
];

const opColor = (op: string) => {
  if (op === "INSERT") return "default" as const;
  if (op === "UPDATE") return "secondary" as const;
  if (op === "DELETE") return "destructive" as const;
  return "outline" as const;
};

const Audit = () => {
  const isMobile = useIsMobile();
  const [opsFilter, setOpsFilter] = useState<string>("all");
  const [finTable, setFinTable] = useState("all");
  const [finStart, setFinStart] = useState("");
  const [finEnd, setFinEnd] = useState("");

  const { data: opsRows = [], isLoading: opsLoading } = useOpsAuditLog(
    opsFilter !== "all" ? { tableName: opsFilter } : undefined
  );
  const { data: ledgerRows = [], isLoading: ledgerLoading } = useLedgerAuditLog();
  const { data: finRows = [], isLoading: finLoading } = useFinancialAuditLog({
    tableName: finTable,
    startDate: finStart || undefined,
    endDate: finEnd || undefined,
  });

  const exportCsv = useCallback(() => {
    if (!finRows.length) return;
    const headers = ["changed_at", "table_name", "operation", "record_id", "changed_by"];
    const csv = [
      headers.join(","),
      ...finRows.map((r) =>
        [r.changed_at, r.table_name, r.operation, r.record_id, r.changed_by ?? ""]
          .map((v) => `"${v}"`)
          .join(",")
      ),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `financial-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [finRows]);

  return (
    <RequirePermission anyOf={["view_financials", "approve_bills", "approve_payments"]}>
      <div className="space-y-4">
        <h1 className="text-2xl font-bold tracking-tight">Audit Logs</h1>
        <Tabs defaultValue="financial">
          <TabsList>
            <TabsTrigger value="financial">Financial</TabsTrigger>
            <TabsTrigger value="ops">Operations</TabsTrigger>
            <TabsTrigger value="ledger">Ledger</TabsTrigger>
          </TabsList>

          {/* Financial Audit Tab */}
          <TabsContent value="financial" className="space-y-3">
            <div className={`flex ${isMobile ? "flex-col" : "flex-wrap items-end"} gap-3`}>
              <div className={isMobile ? "w-full" : ""}>
                <label className="text-xs font-medium text-muted-foreground">Table</label>
                <Select value={finTable} onValueChange={setFinTable}>
                  <SelectTrigger className={isMobile ? "w-full" : "w-48"}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FINANCIAL_TABLES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className={isMobile ? "grid grid-cols-2 gap-3" : "contents"}>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">From</label>
                  <Input type="date" value={finStart} onChange={(e) => setFinStart(e.target.value)} className={isMobile ? "w-full" : "w-40"} />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">To</label>
                  <Input type="date" value={finEnd} onChange={(e) => setFinEnd(e.target.value)} className={isMobile ? "w-full" : "w-40"} />
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={exportCsv} disabled={!finRows.length} className={isMobile ? "w-full" : ""}>
                <Download className="h-4 w-4 mr-1" /> Export CSV
              </Button>
            </div>

            {finLoading ? (
              <p className="p-4 text-muted-foreground">Loading…</p>
            ) : !finRows.length ? (
              <p className="p-4 text-muted-foreground">No financial audit entries found.</p>
            ) : isMobile ? (
              <div className="space-y-3">
                {finRows.map((r) => (
                  <Card key={r.id}>
                    <CardContent className="p-3">
                      <div className="flex items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <Badge variant={opColor(r.operation)}>{r.operation}</Badge>
                            <span className="text-sm font-medium">{r.table_name}</span>
                          </div>
                          <p className="font-mono text-[10px] text-muted-foreground mt-1">
                            {r.record_id.slice(0, 8)} · by {r.changed_by?.slice(0, 8) ?? "system"}
                          </p>
                        </div>
                        <span className="text-xs text-muted-foreground shrink-0 ml-2">
                          {format(new Date(r.changed_at), "MMM d, HH:mm")}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>Table</TableHead>
                    <TableHead>Operation</TableHead>
                    <TableHead>Record ID</TableHead>
                    <TableHead>Changed By</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {finRows.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                        {format(new Date(r.changed_at), "MMM d, yyyy HH:mm:ss")}
                      </TableCell>
                      <TableCell className="font-medium text-sm">{r.table_name}</TableCell>
                      <TableCell>
                        <Badge variant={opColor(r.operation)}>{r.operation}</Badge>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{r.record_id.slice(0, 8)}</TableCell>
                      <TableCell className="font-mono text-xs">{r.changed_by?.slice(0, 8) ?? "system"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TabsContent>

          {/* Operations Tab */}
          <TabsContent value="ops" className="space-y-3">
            <Select value={opsFilter} onValueChange={setOpsFilter}>
              <SelectTrigger className={isMobile ? "w-full" : "w-48"}>
                <SelectValue placeholder="Filter by table" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tables</SelectItem>
                <SelectItem value="purchase_orders">Purchase Orders</SelectItem>
                <SelectItem value="job_orders">Job Orders</SelectItem>
                <SelectItem value="change_orders">Change Orders</SelectItem>
              </SelectContent>
            </Select>
            <AuditLogTable rows={opsRows} isLoading={opsLoading} variant="ops" />
          </TabsContent>

          {/* Ledger Tab */}
          <TabsContent value="ledger">
            <AuditLogTable rows={ledgerRows} isLoading={ledgerLoading} variant="ledger" />
          </TabsContent>
        </Tabs>
      </div>
    </RequirePermission>
  );
};

export default Audit;